import React, { Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useManagerRole } from '@/hooks/useManagerRole';

interface ManagerRouteWrapperProps {
  children: React.ReactNode;
}

/**
 * Route wrapper that restricts access to users with manager-level permissions.
 * Redirects non-managers to /tracker/log.
 */
export function ManagerRouteWrapper({ children }: ManagerRouteWrapperProps) {
  const { user, loading: authLoading } = useAuth();
  const { isManager, loading: managerLoading } = useManagerRole();

  // Show loading state while checking auth and manager status
  if (authLoading || managerLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  // Redirect to auth if not logged in
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Redirect non-managers to the activity log
  if (!isManager) {
    console.log('🔐 ManagerRouteWrapper: User is not a manager, redirecting to /tracker/log');
    return <Navigate to="/tracker/log" replace />;
  }

  // ROUTE_COMPONENTS are React.lazy() components; ensure we have a Suspense boundary here.
  return (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}
